/**
 * Every line by name, with what the office calls it — so a call that says `gsm-goip-3` is
 * shown as "SIM 3, A1" wherever a line is named.
 *
 * Read once per console session and cached: the list changes when an administrator adds a
 * gateway, which is not something a dispatcher's screen needs to notice within the second.
 */

import { useEffect, useState } from 'react';

import { request, type Narrow, type Schema } from '@taxi/api-client';

import type { PhoneLineKind } from '../settings/telephony/lines';

export type LineName = Narrow<Schema<'LineName'>, { kind: PhoneLineKind }>;

let cached: Promise<LineName[]> | null = null;

/** The list, fetched the first time anybody asks and shared after. */
export function lineNames(): Promise<LineName[]> {
  cached ??= request<LineName[]>('/api/telephony/lines').catch(() => {
    // A firm with no telephony, or none listed: every name is shown as it is. The next
    // asker tries again rather than inheriting a failed fetch for the session.
    cached = null;
    return [];
  });

  return cached;
}

/** Forgets the list, so the next asker fetches it again. For the page that edits it. */
export function forgetLineNames(): void {
  cached = null;
}

/** The list, as state: empty until it arrives, and for a firm with none listed. */
export function useLineNames(): LineName[] {
  const [lines, setLines] = useState<LineName[]>([]);

  useEffect(() => {
    let cancelled = false;

    void lineNames().then((list) => {
      if (!cancelled) setLines(list);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return lines;
}

/**
 * A line's label by its name, or the name itself for a line nobody has listed.
 *
 * Returns a function rather than the list, because what every caller wants is to show one
 * name; the list behind it is the hook's business.
 */
export function useLineLabel(): (name: string | null) => string | null {
  const lines = useLineNames();

  return (name) => {
    if (name === null) return null;

    const line = lines.find((l) => l.name.toLowerCase() === name.toLowerCase());

    return line === undefined ? name : line.label;
  };
}
